import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { format, parseISO } from "date-fns";
import { AlertTriangle, ArrowLeftRight, Clock, Mail, Phone, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { RefereeSelectItems } from "@/components/calendar/RefereeSelectItems";

export function NewBookingDialog({
  newSlot, setNewSlot, nb, setNb, nbBusy,
  submitNewBooking, date, user, teams, eligibleReferees, refereeBusy = [],
  showAllTeams, setShowAllTeams,
  clashDetail, onDismissClash,
}) {
  const navigate = useNavigate();
  return (
    <Dialog open={!!newSlot} onOpenChange={(v)=>{ if (!v) setNewSlot(null); }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display tracking-tight text-2xl">New booking</DialogTitle></DialogHeader>
        {newSlot && (
          <div className="space-y-4" data-testid="quick-book-modal">
            {clashDetail && (
              <div className="rounded-md border border-amber-500/50 bg-amber-500/10 p-4 relative" data-testid="clash-panel">
                <button
                  type="button"
                  onClick={onDismissClash}
                  aria-label="Dismiss clash notice"
                  data-testid="clash-panel-dismiss"
                  className="absolute top-2 right-2 p-1 rounded hover:bg-white/10 opacity-70 hover:opacity-100"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                  <div className="min-w-0">
                    <div className="text-[10px] tracking-widest uppercase text-amber-300">{clashDetail.team_clash ? (clashDetail.team_clash_scope === "same_day" ? "Team already booked today" : "Team already booked") : "Slot already taken"}</div>
                    <div className="font-display font-bold text-lg mt-0.5" data-testid="clash-holder">
                      {clashDetail.team_name || clashDetail.user_name || "Another coach"}
                      {clashDetail.age_group && <span className="opacity-60 font-normal text-sm ml-2">· {clashDetail.age_group}</span>}
                    </div>
                    {clashDetail.team_clash && (
                      <div className="text-xs mt-1 text-amber-200" data-testid="clash-team-note">
                        <strong>{clashDetail.team_clash_team || "This team"}</strong> already has a booking on this day{clashDetail.team_clash_scope === "same_day" ? " (any pitch, any venue)" : " that overlaps this time"} — a team can only be booked once per day. Pick a different date or cancel the existing booking first.
                      </div>
                    )}
                    <div className="text-xs opacity-80 mt-1" data-testid="clash-slot">
                      {clashDetail.start}–{clashDetail.end}
                      {clashDetail.session_type && <> · {clashDetail.session_type === "game" ? (clashDetail.match_type || "game") : clashDetail.session_type}</>}
                      {clashDetail.opponent && <> · vs {clashDetail.opponent}</>}
                    </div>
                    {(clashDetail.user_name || clashDetail.user_email || clashDetail.user_phone) && (
                      <div className="mt-3 pt-3 border-t border-amber-500/30 text-xs space-y-1" data-testid="clash-contact">
                        {clashDetail.user_name && (
                          <div><span className="opacity-60 uppercase tracking-widest text-[10px] mr-2">Coach</span><strong>{clashDetail.user_name}</strong></div>
                        )}
                        {clashDetail.user_phone && (
                          <div className="flex items-center gap-2">
                            <Phone className="w-3 h-3 opacity-60" />
                            <a href={`tel:${clashDetail.user_phone}`} className="hover:text-amber-200 hover:underline" data-testid="clash-phone-link">{clashDetail.user_phone}</a>
                          </div>
                        )}
                        {clashDetail.user_email && (
                          <div className="flex items-center gap-2">
                            <Mail className="w-3 h-3 opacity-60" />
                            <a href={`mailto:${clashDetail.user_email}`} className="hover:text-amber-200 hover:underline break-all" data-testid="clash-email-link">{clashDetail.user_email}</a>
                          </div>
                        )}
                      </div>
                    )}
                    <div className="mt-3 text-[11px] opacity-70">Pick a different pitch or reach out to the current holder to see if they'd swap.</div>
                    {Array.isArray(clashDetail.suggestions) && clashDetail.suggestions.length > 0 && (
                      <div className="mt-3" data-testid="clash-suggestions">
                        <div className="text-[9px] tracking-widest uppercase opacity-70 mb-1.5 flex items-center gap-1.5">
                          <Clock className="w-3 h-3" /> Free on this pitch today
                        </div>
                        <div className="flex gap-1.5 flex-wrap">
                          {clashDetail.suggestions.map((s) => (
                            <button
                              key={`${s.start}-${s.end}`}
                              type="button"
                              onClick={() => {
                                // Retarget the New Booking dialog to the suggested window
                                // and dismiss the clash panel — nb.duration is derived from
                                // newSlot on submit so it stays in sync automatically.
                                setNewSlot({ ...newSlot, hour: s.start_hour, min: s.start_min });
                                onDismissClash?.();
                              }}
                              data-testid={`clash-suggestion-${s.start}`}
                              className="text-[11px] tracking-wider px-3 py-1.5 rounded-md border border-[#83B68B]/60 text-[#83B68B] hover:bg-[#83B68B]/15 transition-colors inline-flex items-center gap-1"
                            >
                              {s.start}–{s.end}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    {clashDetail.id && clashDetail.user_email !== user?.email && (
                      <button
                        type="button"
                        onClick={() => {
                          // Close the New Booking dialog then hop to My Bookings with the
                          // target pre-selected — the OfferSwapDialog picks it up on mount.
                          setNewSlot(null);
                          navigate(`/app/bookings?swap_target=${clashDetail.id}`);
                        }}
                        data-testid="clash-propose-swap-btn"
                        className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-md border border-[#83B68B]/60 text-[#83B68B] hover:bg-[#83B68B]/10 text-[10px] tracking-widest uppercase font-display font-bold"
                      >
                        <ArrowLeftRight className="w-3.5 h-3.5" /> Propose swap
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}
            <div className="flex items-baseline justify-between border-b border-border pb-3">
              <div>
                <div className="font-display font-bold text-lg">{newSlot.pitch.name}</div>
                <div className="text-xs opacity-60">{format(date, "EEEE, d MMMM yyyy")}</div>
              </div>
              <div className="text-right">
                <div className="font-display font-black text-2xl text-[#83B68B]" data-testid="qb-time-headline">
                  {(() => {
                    const warm = nb.session_type === "game" ? (nb.warm_up_min || 0) : 0;
                    const cool = nb.session_type === "game" ? (nb.cool_down_min || 0) : 0;
                    const s = newSlot.hour * 60 + newSlot.min - warm;
                    const e = newSlot.hour * 60 + newSlot.min + nb.duration + cool;
                    const fmt = (m) => `${String(Math.floor(m/60)).padStart(2,"0")}:${String(m%60).padStart(2,"0")}`;
                    return `${fmt(s)} — ${fmt(e)}`;
                  })()}
                </div>
                <div className="text-[10px] tracking-widest uppercase opacity-60" data-testid="qb-time-caption">
                  {(() => {
                    const warm = nb.session_type === "game" ? (nb.warm_up_min || 0) : 0;
                    const cool = nb.session_type === "game" ? (nb.cool_down_min || 0) : 0;
                    if (warm || cool) return `${nb.duration}m game · ${warm ? `+${warm}m warm-up` : ""}${warm && cool ? " " : ""}${cool ? `+${cool}m cool-down` : ""}`;
                    return `${nb.duration} min`;
                  })()}
                </div>
              </div>
            </div>

            <div>
              <Label className="text-[10px] tracking-widest uppercase opacity-70">Duration</Label>
              <div className="grid grid-cols-4 gap-1.5 mt-1.5">
                {[15,30,45,60,75,90,105,120].map(d => (
                  <button key={d} type="button" onClick={()=>setNb({...nb, duration: d})} data-testid={`qb-dur-${d}`}
                    className={`py-2 rounded text-xs font-display tracking-wider ${nb.duration===d ? "bg-[#83B68B] text-white" : "bg-muted opacity-80 border border-border hover:border-[#83B68B]"}`}>
                    {d}m
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-[10px] tracking-widest uppercase opacity-70">Session</Label>
              <div className="grid grid-cols-2 gap-1.5 mt-1.5">
                {[{v:"training",l:"Training"},{v:"game",l:"Game"}].map(o => (
                  <button key={o.v} type="button"
                    onClick={()=>{
                      // Recommend a 15-min warm-up + cool-down the first time the user picks Game
                      // (they can zero it out — see the pre/post chips below). Training keeps zero.
                      const patch = { session_type: o.v };
                      if (o.v === "game" && !nb.warm_up_min && !nb.cool_down_min) {
                        const openMin = (newSlot?.pitch?.open_hour ?? 0) * 60;
                        const slotMin = newSlot ? newSlot.hour * 60 + newSlot.min : openMin + 15;
                        patch.warm_up_min = slotMin - openMin >= 15 ? 15 : 0;
                        patch.cool_down_min = 15;
                      }
                      if (o.v === "training") { patch.warm_up_min = 0; patch.cool_down_min = 0; }
                      setNb({...nb, ...patch});
                    }}
                    data-testid={`qb-session-${o.v}`}
                    className={`py-2 rounded text-xs font-display tracking-widest uppercase ${nb.session_type===o.v ? "bg-[#83B68B] text-white" : "bg-muted opacity-80 border border-border hover:border-[#83B68B]"}`}>
                    {o.l}
                  </button>
                ))}
              </div>
            </div>

            {nb.session_type === "training" && (
              <div data-testid="qb-training-season-section">
                <Label className="text-[10px] tracking-widest uppercase opacity-70">Training season</Label>
                <div className="grid grid-cols-3 gap-1.5 mt-1.5">
                  {[{v:"winter",l:"Winter"},{v:"summer",l:"Summer"},{v:"both",l:"Both"}].map(o => (
                    <button key={o.v} type="button" onClick={()=>setNb({...nb, training_season: o.v})} data-testid={`qb-training-season-${o.v}`}
                      className={`py-2 rounded text-xs font-display tracking-widest uppercase ${nb.training_season===o.v ? "bg-[#83B68B] text-white" : "bg-muted opacity-80 border border-border hover:border-[#83B68B]"}`}>
                      {o.l}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {nb.session_type === "game" && (
              <div data-testid="qb-warmup-section" className="rounded-md border border-[#83B68B]/25 bg-[#83B68B]/[0.03] p-3 space-y-3">
                <div>
                  <Label className="text-[10px] tracking-widest uppercase opacity-70 flex items-center justify-between">
                    <span>Warm-up before <span className="opacity-50">(recommended)</span></span>
                    <span className="opacity-60 tabular-nums">{nb.warm_up_min || 0} min</span>
                  </Label>
                  <div className="grid grid-cols-4 gap-1.5 mt-1.5">
                    {[0, 15, 30, 45].map(m => (
                      <button key={m} type="button" onClick={()=>setNb({...nb, warm_up_min: m})} data-testid={`qb-warmup-${m}`}
                        className={`py-1.5 rounded text-[11px] font-display tracking-wider ${nb.warm_up_min===m ? "bg-[#83B68B] text-white" : "bg-muted opacity-80 border border-border hover:border-[#83B68B]"}`}>
                        {m === 0 ? "None" : `${m}m`}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="text-[10px] tracking-widest uppercase opacity-70 flex items-center justify-between">
                    <span>Cool-down after <span className="opacity-50">(recommended)</span></span>
                    <span className="opacity-60 tabular-nums">{nb.cool_down_min || 0} min</span>
                  </Label>
                  <div className="grid grid-cols-4 gap-1.5 mt-1.5">
                    {[0, 15, 30, 45].map(m => (
                      <button key={m} type="button" onClick={()=>setNb({...nb, cool_down_min: m})} data-testid={`qb-cooldown-${m}`}
                        className={`py-1.5 rounded text-[11px] font-display tracking-wider ${nb.cool_down_min===m ? "bg-[#83B68B] text-white" : "bg-muted opacity-80 border border-border hover:border-[#83B68B]"}`}>
                        {m === 0 ? "None" : `${m}m`}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="text-[10px] opacity-50 leading-relaxed">
                  The pitch is blocked out for warm-up + game + cool-down. Tap "None" to book only the game time.
                </div>
              </div>
            )}

            {nb.session_type === "game" && (
              <>
                <div>
                  <Label className="text-[10px] tracking-widest uppercase opacity-70">Match type</Label>
                  <div className="grid grid-cols-3 gap-1.5 mt-1.5">
                    {[{v:"league",l:"League"},{v:"cup",l:"Cup"},{v:"friendly",l:"Friendly"}].map(o => (
                      <button key={o.v} type="button" onClick={()=>setNb({...nb, match_type: o.v})} data-testid={`qb-match-${o.v}`}
                        className={`py-2 rounded text-xs font-display tracking-widest uppercase ${nb.match_type===o.v ? "bg-[#83B68B] text-white" : "bg-muted opacity-80 border border-border hover:border-[#83B68B]"}`}>
                        {o.l}
                      </button>
                    ))}
                  </div>
                  {nb.match_type === "league" && newSlot?.pitch?.league_curfew_hour != null && newSlot.hour < newSlot.pitch.league_curfew_hour && (
                    <div className={`mt-2 text-xs rounded border p-2 ${user?.role === "admin" ? "border-amber-500/40 bg-amber-500/10 text-amber-200" : "border-red-500/50 bg-red-500/10 text-red-300"}`} data-testid="qb-curfew-warning">
                      {user?.role === "admin"
                        ? `Heads up: league games at this venue normally can't start before ${String(newSlot.pitch.league_curfew_hour).padStart(2, "0")}:00 — you're booking as admin.`
                        : `League games at this venue cannot start before ${String(newSlot.pitch.league_curfew_hour).padStart(2, "0")}:00. Pick a later slot, or choose Cup / Friendly if this isn't a league fixture.`}
                    </div>
                  )}
                </div>
                <div>
                  <Label className="text-[10px] tracking-widest uppercase opacity-70">Away team name <span className="opacity-50">(optional)</span></Label>
                  <Input value={nb.opponent || ""} onChange={e=>setNb({...nb, opponent: e.target.value})} placeholder="e.g. Sawston United" className="mt-1.5" data-testid="qb-away-team" />
                </div>
                {(nb.match_type === "league" || nb.match_type === "cup") && (
                  <div>
                    <Label className="text-[10px] tracking-widest uppercase opacity-70">Referee <span className="text-[#83B68B]">(required)</span></Label>
                    <Select value={nb.referee_id || ""} onValueChange={v=>setNb({...nb, referee_id: v})} disabled={!!nb.referee_tbd}>
                      <SelectTrigger data-testid="qb-referee" className="mt-1.5"><SelectValue placeholder={nb.referee_tbd ? "Referee to be allocated" : "Select referee"} /></SelectTrigger>
                      <SelectContent>
                        <RefereeSelectItems referees={eligibleReferees} busy={refereeBusy} emptyText="No referees set up yet." date={date} isAdmin={user?.role === "admin"} team={teams.find(t => t.id === nb.team_id)}
                          start={newSlot ? newSlot.hour * 60 + newSlot.min - (nb.warm_up_min || 0) : null}
                          end={newSlot ? newSlot.hour * 60 + newSlot.min + (nb.duration || 0) + (nb.cool_down_min || 0) : null} />
                      </SelectContent>
                    </Select>
                    <label className="mt-2 flex items-center gap-2 text-[11px] opacity-80 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        data-testid="qb-referee-tbd"
                        checked={!!nb.referee_tbd}
                        onChange={e=>setNb({...nb, referee_tbd: e.target.checked, referee_id: e.target.checked ? "" : nb.referee_id})}
                        className="accent-[#83B68B] w-3.5 h-3.5"
                      />
                      <span>Referee TBD — book without a ref for now, I'll assign later</span>
                    </label>
                    {/league\s*assigned/i.test((eligibleReferees.find(r => r.id === nb.referee_id) || {}).name || "") && (
                      <div className="mt-2">
                        <Label className="text-[10px] tracking-widest uppercase opacity-70">Referee's actual name <span className="opacity-50">(optional now — you'll be reminded after the game)</span></Label>
                        <Input value={nb.referee_actual_name || ""} onChange={e=>setNb({...nb, referee_actual_name: e.target.value})} placeholder="e.g. Jo Bloggs" data-testid="qb-referee-actual-name" className="mt-1.5" />
                      </div>
                    )}
                  </div>
                )}
              </>
            )}

            <div>
              <div className="flex items-center justify-between gap-3">
                <Label className="text-[10px] tracking-widest uppercase opacity-70">
                  {nb.session_type === "training" && nb.book_mode === "yeargroup" ? "Age group" : "Team"} <span className="text-[#83B68B]">(required)</span>
                </Label>
                <div className="flex items-center gap-2">
                  {nb.session_type === "training" && (
                    <div className="inline-flex items-center rounded-full border border-border overflow-hidden text-[10px] tracking-widest uppercase" data-testid="qb-book-mode">
                      <button
                        type="button"
                        onClick={()=>setNb({...nb, book_mode: "team"})}
                        data-testid="qb-mode-team"
                        className={`px-2.5 py-1 transition-colors ${nb.book_mode !== "yeargroup" ? "bg-[#83B68B] text-white" : "opacity-60 hover:opacity-100"}`}
                      >Team</button>
                      <button
                        type="button"
                        onClick={()=>setNb({...nb, book_mode: "yeargroup", shared_team_ids: []})}
                        data-testid="qb-mode-yeargroup"
                        className={`px-2.5 py-1 transition-colors ${nb.book_mode === "yeargroup" ? "bg-[#83B68B] text-white" : "opacity-60 hover:opacity-100"}`}
                      >Age group</button>
                    </div>
                  )}
                  {user?.role === "admin" && !(nb.session_type === "training" && nb.book_mode === "yeargroup") && (
                    <div className="inline-flex items-center rounded-full border border-border overflow-hidden text-[10px] tracking-widest uppercase" data-testid="qb-teams-scope">
                      <button
                        type="button"
                        onClick={() => setShowAllTeams(false)}
                        data-testid="qb-scope-mine"
                        className={`px-2.5 py-1 transition-colors ${!showAllTeams ? "bg-[#83B68B] text-white" : "opacity-60 hover:opacity-100"}`}
                      >Mine</button>
                      <button
                        type="button"
                        onClick={() => setShowAllTeams(true)}
                        data-testid="qb-show-all-teams"
                        className={`px-2.5 py-1 transition-colors ${showAllTeams ? "bg-[#83B68B] text-white" : "opacity-60 hover:opacity-100"}`}
                      >All club</button>
                    </div>
                  )}
                </div>
              </div>
              {nb.session_type === "training" && nb.book_mode === "yeargroup" ? (
                <Select value={nb.age_group} onValueChange={v=>setNb({...nb, age_group: v})}>
                  <SelectTrigger data-testid="qb-age-group" className="mt-1.5"><SelectValue placeholder="Select an age group (e.g. U7)" /></SelectTrigger>
                  <SelectContent>
                    {["U6","U7","U8","U9","U10","U11","U12","U13","U14","U15","U16","U17","U18","Adults"].map(a => (
                      <SelectItem key={a} value={a}>{a}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Select value={nb.team_id} onValueChange={v=>setNb({...nb, team_id: v, shared_team_ids: (nb.shared_team_ids || []).filter(x => x !== v)})}>
                  <SelectTrigger data-testid="qb-team" className="mt-1.5"><SelectValue placeholder={teams.length === 0 ? "No teams found" : "Select a team"} /></SelectTrigger>
                  <SelectContent>
                    {teams.length === 0 && <div className="px-3 py-2 text-xs opacity-50">No teams. {user?.role === "admin" ? "Try flipping to 'All club teams' above, or create one on the Teams page." : "Ask an admin to add you as Lead Coach."}</div>}
                    {teams.filter(t => t && t.id).map(t => <SelectItem key={t.id} value={t.id}>{t.name} · {t.age_group}{t.coach_name && showAllTeams ? ` · ${t.coach_name}` : ""}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
              {nb.session_type === "training" && nb.book_mode === "yeargroup" && (
                <div className="text-[10px] opacity-50 mt-1.5 leading-relaxed">
                  Every coach on any {nb.age_group || "age-group"} team will see this in their My Bookings. The booker, admin and Year Group Lead can amend or cancel.
                </div>
              )}
            </div>

            {nb.session_type === "training" && nb.book_mode !== "yeargroup" && nb.team_id && (
              <div data-testid="qb-share-section">
                <div className="flex items-center justify-between">
                  <Label className="text-[10px] tracking-widest uppercase opacity-70">Share pitch with <span className="opacity-50">(up to 3 other teams)</span></Label>
                  <span className="text-[10px] tracking-widest uppercase opacity-60 tabular-nums" data-testid="qb-share-count">{(nb.shared_team_ids || []).length}/3</span>
                </div>
                <p className="text-[11px] opacity-50 mt-1">Add other squads who'll be on the same pitch at the same time — helpful for split-age training sessions.</p>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {teams.filter(t => t.id !== nb.team_id).length === 0 && (
                    <div className="text-xs opacity-50 py-1">No other teams to share with.</div>
                  )}
                  {teams.filter(t => t.id !== nb.team_id).map(t => {
                    const chosen = (nb.shared_team_ids || []).includes(t.id);
                    const atCap = !chosen && (nb.shared_team_ids || []).length >= 3;
                    return (
                      <button
                        key={t.id}
                        type="button"
                        disabled={atCap}
                        onClick={() => {
                          const current = nb.shared_team_ids || [];
                          const next = chosen ? current.filter(x => x !== t.id) : [...current, t.id];
                          setNb({ ...nb, shared_team_ids: next });
                        }}
                        data-testid={`qb-share-team-${t.id}`}
                        className={`px-2.5 py-1 rounded-full text-[11px] border transition-colors ${
                          chosen
                            ? "bg-[#83B68B] text-white border-[#83B68B]"
                            : atCap
                            ? "border-border opacity-30 cursor-not-allowed"
                            : "border-border opacity-80 hover:border-[#83B68B] hover:opacity-100"
                        }`}
                      >
                        {t.name} · {t.age_group}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <div>
              <Label className="text-[10px] tracking-widest uppercase opacity-70">Notes</Label>
              <Textarea value={nb.notes} onChange={e=>setNb({...nb, notes: e.target.value})} rows={2} placeholder="Anything the club should know" className="mt-1.5" />
            </div>

            <div>
              <Label className="text-[10px] tracking-widest uppercase opacity-70">Repeat weekly</Label>
              <div className="grid grid-cols-5 gap-1.5 mt-1.5">
                {[1,2,4,8,12].map(n => (
                  <button key={n} type="button" onClick={()=>setNb({...nb, repeat_weeks: n})}
                    className={`py-2 rounded text-xs font-display tracking-widest uppercase ${nb.repeat_weeks===n ? "bg-[#83B68B] text-white" : "bg-muted opacity-80 border border-border hover:border-[#83B68B]"}`}>
                    {n === 1 ? "Once" : `${n} wks`}
                  </button>
                ))}
              </div>
            </div>

            <div className="dialog-actions">
              <button onClick={submitNewBooking} disabled={nbBusy} data-testid="qb-submit" className="btn-volt w-full py-3 rounded-md disabled:opacity-40">
                {nbBusy ? "Booking…" : "Confirm booking"}
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
